// @ts-check
// src/lib/connection-state.js
// 2026-05-29 EXTRACTED from src/App.jsx (C1 refactor — connection/WS module-level
// mutable state) as a behavior-preserving TRANSFORM.
//
// Why extract:
//   - These five bindings are the live connection/WebSocket state shared across
//     apiGet / apiSave / connectWebSocket and the page-level components that
//     render it (SyncStatus pending count, LoginScreen server status). Keeping
//     them inside the 6741-LOC App.jsx monolith forces every reader/writer to
//     live in-file. Hoisting them into a tiny module-singleton lets the network
//     layer + leaf components import the SAME state без App.jsx coupling
//     (build-time graph, test-time mocking, circular-dependency risk) — same
//     precedent as src/utils/session.js.
//
// Read/write contract (ESM live bindings):
//   - READS: import the exported binding directly. ESM live bindings reflect
//     in-module updates, so a reader sees the current value AFTER a setter runs.
//     (No accessor function needed for reads — `import { serverOnline }` is live.)
//   - WRITES: you CANNOT assign to an imported binding from another module
//     ("Cannot assign to 'serverOnline' because it is an import"). Every write
//     MUST go through the matching setter exported here.
//
//   - `pendingSaves` is the ONE exception: it is a `const` Map, never reassigned —
//     only mutated in place via .set/.delete/.clear/.has/.size. The imported
//     reference points at the SAME Map object across modules, so no setter is
//     needed (and adding one would be a footgun — it must stay a single shared
//     instance, like the old in-file `const pendingSaves`).
//
// Threading model: single-threaded JS — no race on these mutations.

// 2026-08-31 (Phase 1 session TTL fix-round, FR5 — cursor#4): any successful server
// response is a stronger, more IMMEDIATE "we're back online" signal than waiting for the
// next TTL trigger (interval is up to 60s away). setServerOnline(true) is the single
// point every apiGet/apiSave success already funnels through (~9 call sites in
// api-client.js) — clearing suspicion here covers all of them for free, instead of
// touching each call site. clearLocalSuspicion() is idempotent/cheap (early-return no-op
// when nothing is standing), so calling it on every online-flip (which can be frequent)
// is safe. Deliberately NOT on setServerOnline(false): going offline is not evidence the
// session is fine — that stays exclusively checkLocalSessionExpiry's job (probe-first).
import { clearLocalSuspicion } from './session-expiry.js';
import { API_BASE } from './endpoints.js';
import { stampNow, elapsedSince } from './elapsed.js';
import { beginClockSample, endClockSample, noteHttpResponseSample } from './server-clock.js';
// 2026-08-31 (Phase 2 fix-round, ФР4 — координатор C1): getSessionToken() feeds
// computeSnapshot() below — no cycle (utils/session.js imports nothing itself).
import { getSessionToken } from '../utils/session.js';

// ── serverOnline ─────────────────────────────────────────────────────────────
// Tri-state-ish boolean: true once a request/WS confirms the server is reachable,
// flipped false on a failed apiGet/apiSave. Drives reconnect-flush + poll gating.
export let serverOnline = false;
/**
 * @param {boolean} v
 * @returns {void}
 */
export function setServerOnline(v) {
  serverOnline = v;
  if (v) {
    clearLocalSuspicion();
    // 2026-08-31 (Phase 2 «Офлайн видим», П1): any success clears the debounced
    // offline verdict too, not just the raw flag — resetStrikes() below is the
    // ONLY place strikes/offlineConfirmed reset, so every apiGet/apiSave success
    // (~9 call sites, all funnel through this setter) clears it for free.
    resetStrikes();
    // ФР2 (fix-round, cursor MEDIUM = жюри A L1): normalize status HERE, not just in
    // probeConnection's own ok-branch — every caller of setServerOnline(true) (WS
    // onopen, apiGet/apiSave success, flush's ok-branch, probeConnection) gets the
    // pill's status corrected immediately instead of staying stale (possibly still
    // 'offline'/'banned') for up to 5s until the next watch tick happens to fix it.
    status = (wsConnection && wsConnection.readyState === WebSocket.OPEN) ? 'live' : 'poll';
    // Unconditional notify: resetStrikes() itself only notifies when strikes or
    // offlineConfirmed actually changed (e.g. the very first successful boot with
    // strikes already at 0 changes nothing there) — but `serverOnline`/`status` just
    // flipped, and any subscriber relying on "we just went online" needs a poke
    // regardless of whether the debounced verdict moved.
    notifyConnection();
  } else {
    // 2026-08-31 (Phase 2, П1): the one call site (apiGet's catch, api-client.js:228)
    // reaches here only AFTER its own control /health check already failed too —
    // this is already real evidence, not a first guess. One strike (not a direct
    // offlineConfirmed=true) is the deliberate speed-up: a cold-boot outage becomes
    // visible in ~5-7s (this strike + the next probe/watch-tick failure) instead of
    // waiting for two more independent debounce cycles from scratch.
    recordStrike();
  }
}

// ── pendingSaves ─────────────────────────────────────────────────────────────
// key: endpoint, value: { data, userEmail, retries }.
// Retry queue for saves that failed while offline; flushed when server returns.
// SHARED singleton — mutated in place (.set/.delete/.clear), NEVER reassigned.
// Moved above the connection-watch block below (2026-08-31, Phase 2 П1): its
// computeSnapshot() reads pendingSaves.size and is invoked eagerly at module-load
// time (`let connectionSnapshot = computeSnapshot()`) — needs pendingSaves already
// past its TDZ at that point, so the declaration has to sit textually earlier.
/** @type {Map<string, { data: any, userEmail?: string, retries?: number, deleted?: any }>} */
export const pendingSaves = new Map();

// ── connection watch: strikes / offlineConfirmed / status ──────────────────────
// 2026-08-31 (Phase 2 «Офлайн видим», П1): the incident this phase exists for — a
// payment sat in the offline queue and lived silently in the browser for 5 hours,
// nobody knew. serverOnline flips on the FIRST success/failure signal (noisy — one
// blip shouldn't declare offline), so a debounced verdict sits on top: `strikes`
// counts consecutive failure signals, `offlineConfirmed` is the "for real, not a
// blip" verdict OfflineBanner/blockIfOffline act on. `status` is the SyncStatus
// pill's state, hoisted here from its old component-local useState so App's pulse
// (П4) and the create-gate (П5) read/drive the SAME source of truth SyncStatus
// renders — one module owns the verdict, every reader/writer imports it.
export let strikes = 0;
export let offlineConfirmed = false;
/** @type {'checking'|'live'|'poll'|'offline'|'banned'} */
export let status = 'checking';

// ── subscribe/notify — the one bridge into React ────────────────────────────────
// No useSyncExternalStore precedent in this codebase — subscribers do
// useState(getConnectionSnapshot()) + useEffect(() => subscribeConnection(() =>
// setSnapshot(getConnectionSnapshot()))). The snapshot is a cached PRIMITIVE
// (string), not an object literal: two renders where nothing observable changed
// produce the IDENTICAL string, so React's setState bails via Object.is instead of
// re-rendering every subscriber on every 5s watch tick regardless of whether
// anything moved. `strikes` is deliberately NOT part of it — no subscriber renders
// a strike count, and a lone first strike isn't an observable flip (see
// recordStrike below — it only notifies once it actually confirms offline).
const connectionListeners = new Set();
function computeSnapshot() {
  // ФР4 (fix-round, координатор C1): token presence is part of the primitive — without
  // it, a logout while offlineConfirmed is standing doesn't change the snapshot STRING
  // (only the token changed, no field here reflected that), so OfflineBanner's
  // useState bails via Object.is and never re-renders — the banner stays stuck over
  // LoginScreen until some UNRELATED connection event happens to fire the next notify.
  return `${status}|${pendingSaves.size}|${offlineConfirmed}|${!!getSessionToken()}`;
}
let connectionSnapshot = computeSnapshot();
/** @returns {string} */
export function getConnectionSnapshot() {
  return connectionSnapshot;
}
/**
 * @param {() => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribeConnection(fn) {
  connectionListeners.add(fn);
  return () => connectionListeners.delete(fn);
}
function notifyConnection() {
  connectionSnapshot = computeSnapshot();
  // ФР5 (fix-round, cursor LOW): iterate a SNAPSHOT copy, not the live Set — a listener
  // that synchronously unsubscribes another listener from inside its own callback could
  // otherwise make the live Set skip delivering to that other one in this same tick
  // (Set iteration doesn't yield an item removed after the iterator started but before
  // it reached that item's position).
  for (const fn of [...connectionListeners]) fn();
}

/**
 * A failure signal (probeConnection's non-ok/throw branch, or setServerOnline(false)).
 * Debounced verdict: one strike is "could be a blip", two is "confirmed" — the SAME
 * 2-strike threshold as the pre-existing catch-branch in SyncStatus (its HTTP-branch
 * needed 3 — normalized to 2 here for both, a deliberate fix: both failure paths are
 * now increment-then-check). Only notifies on the strike that actually confirms
 * offline — a lone first strike isn't part of the snapshot (see computeSnapshot
 * above), so notifying then would just be a wasted call to every subscriber.
 * @returns {void}
 */
export function recordStrike() {
  strikes += 1;
  if (strikes >= 2) {
    offlineConfirmed = true;
    status = 'offline';
    notifyConnection();
  }
}

/**
 * @returns {void}
 */
export function resetStrikes() {
  const changed = strikes !== 0 || offlineConfirmed;
  strikes = 0;
  offlineConfirmed = false;
  if (changed) notifyConnection();
}

let probeInFlight = false;
let lastProbeAt = 0;
const PROBE_COOLDOWN_MS = 3000;
const PROBE_JITTER_MAX_MS = 1500;

/**
 * THE single prober — startHealthWatch's 5s tick, the App-level pulse (П4: tab
 * visible/focus/60s-interval), and blockIfOffline's self-heal fire-and-forget all
 * call this SAME function, so a burst of triggers (tab regains focus right as the
 * 5s tick lands) collapses into one real network request via the in-flight guard +
 * cooldown, not three.
 * @returns {Promise<void>}
 */
export async function probeConnection() {
  if (probeInFlight) return;
  if (elapsedSince(lastProbeAt) < PROBE_COOLDOWN_MS) return;
  probeInFlight = true;
  lastProbeAt = stampNow();
  try {
    const jitter = Math.random() * PROBE_JITTER_MAX_MS;
    if (jitter > 0) await new Promise((resolve) => setTimeout(resolve, jitter));
    const started = beginClockSample();
    const res = await fetch(`${API_BASE}/health`, { cache: 'no-store', signal: AbortSignal.timeout(5000) });
    let json = null;
    try {
      if (res && typeof res.json === 'function') json = await res.json();
    } catch { json = null; }
    noteHttpResponseSample(res, json, endClockSample(started), { expectDate: true });
    if (res.ok) {
      // ФР2 (fix-round): setServerOnline(true) normalizes status itself now — this
      // used to duplicate that same line here; removed, it would just be a stale dup.
      setServerOnline(true);
    } else if (res.status === 403) {
      status = 'banned';
      notifyConnection();
    } else {
      // ФР1 (fix-round, cursor HIGH): direct assignment, NOT the setServerOnline(false)
      // setter — that setter itself calls recordStrike() on false, which would double-
      // strike a single failed probe. A stale serverOnline=true otherwise makes
      // startHealthWatch's tick keep taking the 'poll' branch (its own serverOnline
      // gate) forever — nothing else in this failure path would ever flip it back to
      // false, so the watch stops re-probing regularly.
      serverOnline = false;
      recordStrike();
    }
  } catch {
    // Same reasoning as the non-ok branch above — direct assignment, not the setter.
    serverOnline = false;
    recordStrike();
  } finally {
    probeInFlight = false;
  }
}

let healthWatchTimer = null;

/**
 * Idempotent: a second call while the interval is already running is a no-op (both
 * calls get back the SAME stop function) — callers (App.jsx's boot effect) can call
 * this defensively without risking a double interval. Without this stop contract,
 * this would be exactly the PR #688 timer-unmount-hygiene class of bug.
 * @returns {() => void} stopHealthWatch
 */
export function startHealthWatch() {
  if (healthWatchTimer) return stopHealthWatch;
  healthWatchTimer = setInterval(() => {
    // ФР3 (fix-round, координатор C2): offlineConfirmed checked FIRST, ahead of the
    // WS-open branch below — a zombie WS (readyState still OPEN with nothing real
    // behind it) would otherwise make this tick flip status to 'live' every 5s while
    // a failing recovery probe elsewhere flips it back to 'offline' on confirmation —
    // an offline<->live oscillation on the pill (the banner, driven by offlineConfirmed
    // alone, stays stable throughout). Re-probing here (not just trusting the stale WS
    // flag) is also how a real recovery gets noticed even though a zombie WS never
    // reports its own death.
    if (offlineConfirmed) {
      status = 'offline';
      probeConnection();
    } else if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
      status = 'live';
    } else if (serverOnline) {
      status = 'poll';
    } else {
      // Cooldown inside probeConnection (3s) doesn't fight this 5s tick.
      probeConnection();
    }
    notifyConnection();
  }, 5000);
  return stopHealthWatch;
}

/**
 * @returns {void}
 */
export function stopHealthWatch() {
  if (healthWatchTimer) {
    clearInterval(healthWatchTimer);
    healthWatchTimer = null;
  }
}

/**
 * Create/restore gate (Dashboard/CustomerPayments, П5) — same shape as
 * blockIfSessionExpired (session-expiry.js): a confirmed-offline browser must not
 * queue up new optimistic records blind. probeConnection() here is fire-and-forget
 * self-heal for the NEXT attempt, not a wait — same pattern as
 * blockIfSessionExpired's own checkLocalSessionExpiry call.
 * @param {(message: string, level?: string) => void} [notify]
 * @returns {boolean} true — blocked, caller must return.
 */
export function blockIfOffline(notify) {
  if (!offlineConfirmed) return false;
  probeConnection();
  if (notify) notify('No connection - cannot create new records while offline', 'error');
  return true;
}

/**
 * TEST-ONLY. Module-singleton state resists isolation across `it` blocks in the
 * same file — same rationale as App.jsx's `_testResetApiSaveState`.
 * @returns {void}
 */
export function _testResetConnectionState() {
  strikes = 0;
  offlineConfirmed = false;
  status = 'checking';
  probeInFlight = false;
  lastProbeAt = 0;
  // ФР6 (fix-round, координатор C3): direct assignment, NOT setServerOnline(false) — the
  // setter has side effects (recordStrike) this test-only reset must NOT trigger.
  serverOnline = false;
  stopHealthWatch();
  connectionListeners.clear();
  connectionSnapshot = computeSnapshot();
}

// ── WebSocket connection handle ──────────────────────────────────────────────
// The live WebSocket (or null when disconnected). Read to guard against
// double-connect / to check readyState; written when (re)connecting or on close.
/** @type {WebSocket | null} */
export let wsConnection = null;
/**
 * @param {WebSocket | null} v
 * @returns {void}
 */
export function setWsConnection(v) {
  wsConnection = v;
}

// ── WebSocket reconnect timer handle ─────────────────────────────────────────
// setTimeout handle for the pending reconnect (or null when none scheduled).
// Read as a guard (`if (!wsReconnectTimer)`), written when scheduling/clearing.
/** @type {ReturnType<typeof setTimeout> | null} */
export let wsReconnectTimer = null;
/**
 * @param {ReturnType<typeof setTimeout> | null} v
 * @returns {void}
 */
export function setWsReconnectTimer(v) {
  wsReconnectTimer = v;
}

// ── WebSocket reconnect attempt counter ──────────────────────────────────────
// Backoff exponent: read by the delay calc, reset to 0 on successful open,
// incremented on each close/error.
export let wsReconnectAttempts = 0;
/**
 * @param {number} v
 * @returns {void}
 */
export function setWsReconnectAttempts(v) {
  wsReconnectAttempts = v;
}
